import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get("/me/permissions");
      setPermissions(data?.permissions || {});
    } catch {
      setPermissions({});
    }
  }, []);

  const refresh = useCallback(async () => {
    // Skip the /auth/me probe if no token is present (e.g., anonymous visitors on the public marketing route).
    const token = typeof window !== "undefined" ? localStorage.getItem("jt_token") : null;
    if (!token) {
      setUser(null);
      setPermissions({});
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadPerms();
    } catch (e) {
      setUser(null);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [loadPerms]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) {
      localStorage.setItem("jt_token", data.access_token);
    }
    setUser(data.user);
    await loadPerms();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("jt_token");
    setUser(null);
    setPermissions({});
  };

  // Permission check — admins always pass; otherwise the module must include the action.
  const can = useCallback((module, action = "view") => {
    if (!user) return false;
    if ((user.role || "").toLowerCase() === "admin") return true;
    return (permissions[module] || []).includes(action);
  }, [user, permissions]);

  return (
    <AuthContext.Provider value={{ user, permissions, can, loading, login, logout, refresh, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
